/* ============================================================================
   sb-bag.js: the bag. One code path for the preview and the live store: both
   answer Shopify's documented Ajax Cart API (/cart/add.js, /cart/change.js,
   /cart.js) and its shipping-rate endpoints. Shopify prices the bag, so the bag
   shows exactly what checkout will charge.

   What it does
   - Every [data-sb-add-form] submits here instead of reloading the page.
   - The first piece asks first (Theme settings → Bag), every later one goes in.
   - After an add: the corner bag appears (or jumps), the note says so, and only
     then the sound plays (sound never goes before the page's answer: traps.md §14).
   - On the bag page, + / − / Remove change the page at once (VANTA's bag): the
     line, the parcels, the totals, each piece's delivery; Shopify is told in the
     background, in order, and its cart and rates settle what differs. A removed
     piece leaves "Put it back" in its place. The dates are counted in working days.
   - Checkout rings a cash register's ka-ching (a recording), and checkout opens
     650ms later so it is heard.
   - The bag's lines come grouped by parcel (Liquid). Delivery is split per parcel
     and per piece from the supplier's table (snippets/sb-ship-data), and the split
     is shown only when it adds up to Shopify's quote, to the cent.

   Timing is logged from the input event: "⏱ [Timing]".
   ========================================================================== */
(function () {
  "use strict";

  var SB = (window.SB = window.SB || {});
  var cfg = SB.config || {};
  var routes = cfg.routes || { cartAdd: "/cart/add.js", cartChange: "/cart/change.js", cartJs: "/cart.js", cart: "/cart" };
  var bagCfg = cfg.bag || {};
  var strings = cfg.strings || {};
  var state = { count: bagCfg.count || 0, total: 0 };
  var audio = null;

  function json(url, body) {
    // keepalive: a change sent as the visitor leaves the page still reaches Shopify.
    var opts = body
      ? { method: "POST", keepalive: true, headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(body) }
      : { headers: { Accept: "application/json" } };
    return fetch(url, opts).then(function (res) {
      return res.text().then(function (text) {
        var data = text ? JSON.parse(text) : null;
        if (!res.ok) {
          var err = new Error((data && (data.description || data.message)) || "HTTP " + res.status);
          err.status = res.status;
          throw err;
        }
        return data;
      });
    });
  }

  // "{price}" style placeholders, from the strings sb-config.liquid wrote.
  function fill(text, map) {
    return Object.keys(map).reduce(function (s, k) { return s.split("{" + k + "}").join(map[k]); }, text || "");
  }

  /* ---------------------------------------------------------------- painting */
  function paint(cart) {
    state.count = cart.item_count;
    state.total = cart.total_price;
    Array.prototype.forEach.call(document.querySelectorAll("[data-sb-bag-count]"), function (el) { el.textContent = cart.item_count; });
    Array.prototype.forEach.call(document.querySelectorAll("[data-sb-bag-total]"), function (el) { el.textContent = SB.money(cart.total_price); });
    var corner = document.querySelector("[data-sb-bag-corner]");
    if (corner) {
      if (cart.item_count > 0 && corner.hidden) {
        corner.hidden = false;
        corner.classList.add("is-arriving");
        setTimeout(function () { corner.classList.remove("is-arriving"); }, 600);
      } else if (cart.item_count > 0) {
        corner.classList.remove("is-jumping");
        void corner.offsetWidth;
        corner.classList.add("is-jumping");
      } else corner.hidden = true;
    }
    console.log("📦 [State Update]: bag holds", cart.item_count, "for", SB.money(cart.total_price));
  }

  function note(text) {
    var el = document.querySelector("[data-sb-bag-note]");
    if (!el) return;
    el.textContent = text;
    el.classList.add("is-on");
    clearTimeout(el.__t);
    el.__t = setTimeout(function () { el.classList.remove("is-on"); }, 2200);
  }

  /* ---------------------------------------------------------------- the sound */
  // A recorded sound (a cash register: Checkout's ka-ching by default, the owner on 2026-10-16) is an
  // mp3 kept as base64 in a script asset (lab/sound-assets.mjs). The script is fetched once per name;
  // the sound is decoded once the AudioContext exists, so it is ready before it is needed.
  var soundScripts = {};
  var soundBuffers = {};
  function soundData(name, url) {
    if (!url) return Promise.resolve(null);
    if (!soundScripts[name]) {
      soundScripts[name] = new Promise(function (resolve, reject) {
        var have = SB.soundData && SB.soundData[name];
        if (have) return resolve(have);
        console.log("🚀 [API Call]: the", name, "sound");
        var s = document.createElement("script");
        s.src = url;
        s.async = true;
        s.onload = function () { var d = SB.soundData && SB.soundData[name]; return d ? resolve(d) : reject(new Error("no sound data")); };
        s.onerror = function () { reject(new Error("the sound did not load")); };
        document.head.appendChild(s);
      }).catch(function (err) {
        console.error("🛑 [bag]: the", name, "sound failed", err);
        return null;
      });
    }
    return soundScripts[name];
  }
  function soundBuffer(name, url) {
    if (!audio || !url) return Promise.resolve(null);
    if (!soundBuffers[name]) {
      soundBuffers[name] = soundData(name, url).then(function (b64) {
        if (!b64) return null;
        var bin = atob(b64);
        var bytes = new Uint8Array(bin.length);
        for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        // The callback form: Safari before 14.1 never resolves the promise form.
        return new Promise(function (resolve, reject) { audio.decodeAudioData(bytes.buffer, resolve, reject); });
      }).then(function (buffer) {
        if (buffer) console.log("✅ [Sound]:", name, "ready,", buffer.duration.toFixed(2), "s");
        return buffer;
      }).catch(function (err) {
        console.error("🛑 [bag]: the", name, "sound did not decode", err);
        return null;
      });
    }
    return soundBuffers[name];
  }
  function ensureAudio() {
    if (audio) return audio;
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) audio = new Ctx();
    } catch (err) {
      console.error("🛑 [bag]: no audio context", err);
    }
    return audio;
  }

  // The AudioContext is made on the first press (it costs ~100ms once: traps.md §14), then the
  // recorded sounds this page can play are decoded: the add's, and on the bag page Checkout's.
  window.addEventListener("pointerdown", function () {
    var checkout = bagCfg.checkoutSoundUrl && document.querySelector("[data-sb-checkout]");
    if (audio || !(bagCfg.chime || checkout)) return;
    if (!ensureAudio()) return;
    if (bagCfg.chime && bagCfg.soundUrl) soundBuffer(bagCfg.sound, bagCfg.soundUrl);
    if (checkout) soundBuffer(bagCfg.checkoutSound, bagCfg.checkoutSoundUrl);
  }, { once: true, passive: true, capture: true });

  // Back from checkout (the page restored as it was left): Checkout rings again on the next press.
  window.addEventListener("pageshow", function (e) {
    if (!e.persisted) return;
    Array.prototype.forEach.call(document.querySelectorAll("form.sb-bag__layout"), function (f) { f.__ringing = null; });
    Array.prototype.forEach.call(document.querySelectorAll("[data-sb-checkout][aria-busy]"), function (b) { b.removeAttribute("aria-busy"); });
  });
  function play(buffer) {
    var src = audio.createBufferSource();
    var g = audio.createGain();
    src.buffer = buffer;
    g.gain.value = 0.4;
    src.connect(g).connect(audio.destination);
    src.start();
  }

  // One struck partial: a sine that rises in 4ms and dies away exponentially.
  function strike(t, freq, peak, decay) {
    var o = audio.createOscillator();
    var g = audio.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t + decay);
    o.connect(g).connect(audio.destination);
    o.start(t);
    o.stop(t + decay + 0.02);
  }
  // A short burst of filtered noise: the till's drawer, a coin landing in the bag.
  function knock(t, centre, peak, length) {
    var n = Math.floor(audio.sampleRate * length);
    var buf = audio.createBuffer(1, n, audio.sampleRate);
    var ch = buf.getChannelData(0);
    for (var i = 0; i < n; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / n);
    var src = audio.createBufferSource();
    var bp = audio.createBiquadFilter();
    var g = audio.createGain();
    src.buffer = buf;
    bp.type = "bandpass";
    bp.frequency.value = centre;
    bp.Q.value = 1.4;
    g.gain.value = peak;
    src.connect(bp).connect(g).connect(audio.destination);
    src.start(t);
  }
  // The drawn sounds a piece can go in with (Theme settings → Bag → The sound); the cash registers are
  // recordings (above). All of them quiet.
  var SOUNDS = {
    // Coins dropping in: a soft landing, then three small coins, each a little different.
    coins: function (t) {
      strike(t, 170, 0.05, 0.09);
      [0.02, 0.09, 0.15].forEach(function (at, k) {
        var tone = 1 + (k - 1) * 0.06;
        strike(t + at, 4200 * tone, 0.022, 0.16);
        strike(t + at, 6100 * tone, 0.012, 0.11);
        knock(t + at, 6000, 0.02, 0.012);
      });
    },
    // The old chime: two soft sines a fifth apart.
    chime: function (t) {
      [880, 1318.5].forEach(function (f, i) { strike(t + i * 0.09, f, 0.06, 0.5); });
    },
  };
  function chime() {
    if (!bagCfg.chime || !audio || SB.reduce) return;
    try {
      if (bagCfg.soundUrl) {
        // Still decoding (a very fast answer): it plays when ready, unless that is too late to belong
        // to the add.
        var asked = performance.now();
        soundBuffer(bagCfg.sound, bagCfg.soundUrl).then(function (buffer) {
          if (!buffer || performance.now() - asked > 700) return;
          console.log("🔔 [Sound]:", bagCfg.sound);
          play(buffer);
        });
        return;
      }
      var name = SOUNDS[bagCfg.sound] ? bagCfg.sound : "chime";
      console.log("🔔 [Sound]:", name);
      SOUNDS[name](audio.currentTime + 0.01);
    } catch (err) {
      console.error("🛑 [bag]: sound failed", err);
    }
  }

  /* ---------------------------------------------------------------- first piece */
  var dialog = null;
  function askFirst() {
    return new Promise(function (resolve) {
      if (!dialog) {
        dialog = document.createElement("dialog");
        dialog.className = "sb-first";
        dialog.innerHTML =
          '<form method="dialog" class="sb-first__body">' +
          '<h2 class="sb-first__title"></h2><p class="sb-first__text"></p>' +
          '<div class="sb-first__actions"><button class="sb-button" value="yes"></button>' +
          '<button class="sb-first__no" value="no"></button></div></form>';
        document.body.appendChild(dialog);
      }
      dialog.querySelector(".sb-first__title").textContent = strings.firstTitle || "Start your bag?";
      dialog.querySelector(".sb-first__text").textContent = strings.firstBody || "";
      dialog.querySelector("[value=yes]").textContent = strings.firstYes || "Yes";
      dialog.querySelector("[value=no]").textContent = strings.firstNo || "Not yet";
      dialog.returnValue = "";
      dialog.addEventListener("close", function onClose() {
        dialog.removeEventListener("close", onClose);
        console.log("👆 [Trigger]: first-piece dialog answered", dialog.returnValue || "dismissed");
        resolve(dialog.returnValue === "yes");
      });
      dialog.showModal();
    });
  }

  /* ---------------------------------------------------------------- the parcel cap */
  // One parcel holds bagCfg.maxPerParcel pieces at most (Printify's Shopify profiles price 12 per
  // print shop). A parcel is one product type here: the bag asks Shopify's cart only when the bag
  // could be full, so an ordinary add costs no extra request.
  function roomFor(type, quantity) {
    var max = Number(bagCfg.maxPerParcel) || 0;
    if (!max || !type || state.count + quantity <= max) return Promise.resolve(true);
    console.log("🚀 [API Call]: GET", routes.cartJs, "to count the", type, "parcel");
    return json(routes.cartJs).then(function (cart) {
      var inParcel = cart.items.filter(function (l) { return l.product_type === type; }).reduce(function (n, l) { return n + l.quantity; }, 0);
      var ok = inParcel + quantity <= max;
      if (!ok) console.log("🛑 [Blocked]: the", type, "parcel holds", inParcel, "of", max);
      return ok;
    });
  }

  /* ---------------------------------------------------------------- add */
  SB.bag = {
    add: function (variantId, quantity, from) {
      var t0 = (from && from.t0) || performance.now();
      console.log("⚙️ [Function Started]: bag.add", variantId, "×", quantity);
      var ask = bagCfg.confirmFirst && state.count === 0 ? askFirst() : Promise.resolve(true);
      return ask.then(function (yes) {
        if (!yes) return null;
        return roomFor(from && from.type, quantity || 1).then(function (room) { return room ? true : "full"; });
      }).then(function (go) {
        if (go === "full") {
          if (from && from.status) from.status.textContent = strings.parcelFull || "";
          note(strings.parcelFull || "");
          return null;
        }
        if (!go) return null;
        console.log("🚀 [API Call]: POST", routes.cartAdd);
        return json(routes.cartAdd, { items: [{ id: Number(variantId), quantity: quantity || 1 }] })
          .then(function () { return json(routes.cartJs); })
          .then(function (cart) {
            paint(cart);
            note(strings.added || "Added");
            console.log("⏱ [Timing]: add → bag painted in", Math.round(performance.now() - t0), "ms");
            chime();
            // Straight on to the bag (Theme settings → Bag; the owner, 2026-10-24), a beat after the
            // sound starts so it is heard.
            if (bagCfg.goAfterAdd) setTimeout(function () { window.location.href = routes.cart; }, 350);
            return cart;
          });
      }).catch(function (err) {
        console.error("🛑 [bag]: add failed", err);
        if (from && from.status) from.status.textContent = err.message;
        throw err;
      });
    },
    paint: paint,
    sounds: SOUNDS,
  };

  SB.define("add-form", function (form) {
    var status = form.querySelector("[data-sb-add-status]");
    var button = form.querySelector("[data-sb-add-button]");
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var t0 = performance.now();
      var id = form.querySelector('[name="id"]');
      var qty = form.querySelector('[name="quantity"]');
      console.log("👆 [Trigger]: add submitted", id && id.value);
      if (!id || !id.value) return;
      if (button) button.setAttribute("aria-busy", "true");
      var host = form.closest("[data-sb-type]");
      SB.bag.add(id.value, qty ? Number(qty.value) : 1, { t0: t0, status: status, type: host ? host.getAttribute("data-sb-type") : "" })
        .then(function (cart) { if (status && cart) status.textContent = ""; })
        .catch(function () {})
        .then(function () { if (button) button.removeAttribute("aria-busy"); });
    });
  });

  /* ---------------------------------------------------------------- the bag page */
  function workingDaysFrom(date, n) {
    var d = new Date(date.getTime());
    var left = n;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) left--;
    }
    return d;
  }
  function isRest(d) {
    var wd = d.getDay();
    return wd === 0 || wd === 6;
  }
  function sameDay(a, b) {
    return a.getTime() === b.getTime();
  }
  function dayLabel(d) {
    return d.toLocaleDateString(document.documentElement.lang || undefined, { weekday: "short", day: "numeric", month: "short" });
  }

  // The bag page answers in place, as VANTA's bag did (the owner, 2026-10-16: removing a piece
  // rebuilt the whole page, the strip from its first day, and it was slow). + / − / Remove change the
  // line, the parcels, the totals and each piece's delivery at once, from the supplier's table;
  // Shopify is told in the background, one change after another, and its own answers then settle
  // anything that differs: the line prices from the cart, the delivery from the rates.
  SB.define("bag-page", function (root) {
    console.log("⚙️ [Function Started]: bag-page");
    var printDays = (root.getAttribute("data-print-days") || "0,0").split(",").map(Number);
    // Days on the way set in the bag section (Shopify's profile holds none): used while Shopify sends
    // no delivery days of its own. 0 and 0: no dates.
    var shipDays = (root.getAttribute("data-ship-days") || "0,0").split(",").map(Number);
    var setDays = shipDays[1] > 0 ? shipDays : null;
    var form = root.querySelector("form.sb-bag__layout");
    var summary = root.querySelector(".sb-bag__summary");
    var emptyEl = root.querySelector("[data-sb-empty]");
    var shipTo = root.querySelector("[data-sb-ship-to]");
    var shipPrice = root.querySelector("[data-sb-ship-price]");
    var totalEl = root.querySelector("[data-sb-total]");
    var subtotalEl = root.querySelector("[data-sb-subtotal]");
    var parcelsEl = root.querySelector("[data-sb-parcels]");
    var arrives = root.querySelector("[data-sb-arrives]");
    var fullEl = root.querySelector("[data-sb-full]");
    var when = root.querySelector("[data-sb-when]");
    var daysEl = root.querySelector("[data-sb-days]");
    var tipEl = root.querySelector("[data-sb-day-tip]");
    var listEl = root.querySelector("[data-sb-parcel-list]");
    var book = root.querySelector("[data-sb-book]");
    var bookList = root.querySelector("[data-sb-book-list]");
    var rules = [];
    var ship = { groups: {}, own: {} };
    try {
      rules = JSON.parse((root.querySelector("[data-sb-parcel-rules]") || {}).textContent || "[]");
    } catch (err) {
      console.error("🛑 [bag-page]: parcel rules are invalid", err);
    }
    try {
      var shipEl = root.querySelector("[data-sb-ship-data]");
      if (shipEl) ship = JSON.parse(shipEl.textContent);
    } catch (err) {
      console.error("🛑 [bag-page]: the delivery table is invalid", err);
    }
    var country = shipTo ? shipTo.value : cfg.country || "";
    // What Shopify last said, per country: no delivery there, or its table and ours disagree (then
    // the page waits for Shopify's number instead of showing ours first).
    var none = {};
    var trusted = {};
    var days = setDays;
    var gen = 0;

    // Shopify is told in order: each change waits for the one before it.
    var queue = Promise.resolve();
    var busy = 0;
    function send(task) {
      busy++;
      var run = queue.then(task);
      function done() { busy--; }
      queue = run.then(done, done);
      return run;
    }

    function qtyOf(li) {
      return Number(li.getAttribute("data-qty")) || 0;
    }
    function money(cents) {
      return SB.money(cents);
    }
    function failed() {
      if (!fullEl) return;
      fullEl.textContent = strings.failed || "";
      fullEl.hidden = false;
      clearTimeout(fullEl.__t);
      fullEl.__t = setTimeout(function () { fullEl.hidden = true; }, 5000);
    }

    // The parcels with pieces in them: each with its lines, its make days (a "Ships together"
    // block's own, else the section's) and its name as the page shows it.
    function parcels() {
      return Array.prototype.map.call(root.querySelectorAll("[data-sb-parcel]"), function (el) {
        var key = el.getAttribute("data-sb-parcel");
        var rule = key.indexOf("rule:") === 0 ? rules.find(function (r) { return "rule:" + r.name === key; }) : null;
        var lines = Array.prototype.map.call(el.querySelectorAll("[data-sb-line]"), function (li) {
          return { el: li, handle: li.getAttribute("data-handle"), qty: qtyOf(li), unit: Number(li.getAttribute("data-unit")) || 0, title: li.getAttribute("data-title") || "" };
        });
        var label = el.querySelector(".sb-parcel__label");
        var kind = el.querySelector(".sb-parcel__kind");
        var name = [label && label.textContent.trim(), kind && kind.textContent.trim()].filter(Boolean).join(" · ");
        return {
          el: el,
          key: key,
          name: name || key,
          lines: lines,
          pieces: lines.reduce(function (s, l) { return s + l.qty; }, 0),
          print: rule && rule.print && rule.print[1] ? rule.print : printDays,
        };
      }).filter(function (p) { return p.pieces > 0; });
    }

    // Parcel 1, 2, 3 again after a parcel empties ("One parcel" when one is left); an emptied parcel
    // keeps only its "Put it back".
    function renumber() {
      var boxes = Array.prototype.slice.call(root.querySelectorAll("[data-sb-parcel]"));
      var live = boxes.filter(function (el) { return el.querySelector("[data-sb-line]"); });
      boxes.forEach(function (el) {
        var at = live.indexOf(el);
        var pieces = Array.prototype.reduce.call(el.querySelectorAll("[data-sb-line]"), function (s, li) { return s + qtyOf(li); }, 0);
        var label = el.querySelector(".sb-parcel__label");
        var how = el.querySelector(".sb-parcel__how");
        el.classList.toggle("is-empty", at < 0);
        if (label) label.textContent = at < 0 ? "" : live.length === 1 ? strings.parcelOnly : fill(strings.parcelName, { n: at + 1 });
        if (how) how.textContent = at < 0 ? "" : pieces === 1 ? strings.parcelAlone : fill(strings.parcelTogether, { n: pieces });
      });
      return live.length;
    }

    // The supplier's parcel maths, as the store's shipping profiles charge it: in a parcel the
    // dearest first-piece premium is paid once and every piece pays the each-more rate. Each line
    // gets what it adds; false when a parcel or a country is missing from the table.
    //
    // A Shopify profile priced by the order's total instead (tiers: [from, to, price] in cents per
    // country, read from Settings → Shipping and delivery): the parcel pays its tier once, and no piece
    // has a share of its own.
    function price(list) {
      var known = true;
      list.forEach(function (p) {
        var g = ship.groups && ship.groups[p.key];
        p.transit = g && g.transit ? g.transit[country] || g.transit.ROW || null : null;
        if (!g) { known = false; return; }
        if (g.tiers) {
          var sub = p.lines.reduce(function (s, l) { return s + l.unit * l.qty; }, 0);
          var tier = (g.tiers[country] || g.tiers.ROW || []).find(function (t) { return sub >= t[0] && (t[1] === null || sub <= t[1]); });
          p.tiered = true;
          p.lines.forEach(function (l) { l.ship = null; });
          if (!tier) { known = false; return; }
          p.price = tier[2];
          return;
        }
        var premium = 0;
        var carrier = null;
        p.lines.forEach(function (l) {
          var own = ship.own && ship.own[l.handle];
          l.rate = (own && (own[country] || own.ROW)) || g.rates[country] || g.rates.ROW || null;
          if (!l.rate) { known = false; return; }
          if (l.rate[0] - l.rate[1] > premium) { premium = l.rate[0] - l.rate[1]; carrier = l; }
        });
        if (!known) return;
        p.price = premium;
        p.lines.forEach(function (l) {
          l.first = l === carrier;
          l.ship = l.rate[1] * l.qty + (l.first ? premium : 0);
          p.price += l.rate[1] * l.qty;
        });
      });
      return known;
    }

    // What each parcel and each piece adds, and the sum piece by piece: shown only when the split
    // adds up to the delivery shown (ours at once, then Shopify's).
    function paintSplit(list, known, cents) {
      var sum = known ? list.reduce(function (s, p) { return s + p.price; }, 0) : -1;
      var show = known && cents > 0 && sum === cents;
      root.querySelectorAll("[data-sb-parcel]").forEach(function (el) {
        var head = el.querySelector("[data-sb-parcel-ship]");
        if (head) head.hidden = true;
      });
      list.forEach(function (p) {
        var head = p.el.querySelector("[data-sb-parcel-ship]");
        if (head) {
          head.textContent = show ? fill(strings.parcelShip, { price: money(p.price) }) : "";
          head.hidden = !show;
        }
        p.lines.forEach(function (l) {
          var el = l.el.querySelector("[data-sb-line-ship]");
          if (!el) return;
          var own = show && l.ship !== null && l.ship !== undefined;
          el.textContent = own ? fill(strings.lineShip, { price: money(l.ship) }) : "";
          el.hidden = !own;
        });
      });
      if (!book || !bookList) return show;
      // Piece by piece only where pieces have shares: a profile priced by the order's total has none.
      var shares = list.some(function (p) { return !p.tiered; });
      book.hidden = !(show && shares);
      if (!shares) { bookList.textContent = ""; return show; }
      bookList.textContent = "";
      if (!show) return show;
      // Every piece on a row of its own: the first piece of its parcel at the full rate, the rest at
      // the smaller one. Titles are the merchant's words: set as text, never as HTML.
      list.forEach(function (p) {
        var box = document.createElement("div");
        box.className = "sb-book__parcel";
        var head = document.createElement("p");
        head.className = "sb-book__row sb-book__row--head";
        // The head: the parcel, when it arrives, what its delivery costs, all on the first line and in
        // the same clear type (the owner, 2026-10-16: the date as plain to read as the price).
        head.appendChild(document.createElement("span")).textContent = p.name;
        var date = head.appendChild(document.createElement("span"));
        date.className = "sb-book__date";
        date.textContent = p.from && p.to ? fill(strings.arrives, { from: dayLabel(p.from), to: dayLabel(p.to) }) : "";
        head.appendChild(document.createElement("span")).textContent = money(p.price);
        box.appendChild(head);
        var ul = document.createElement("ul");
        ul.className = "sb-book__pieces";
        ul.setAttribute("role", "list");
        p.lines.forEach(function (l) {
          if (p.tiered) return;
          for (var k = 0; k < l.qty; k++) {
            var first = l.first && k === 0;
            var li = document.createElement("li");
            li.className = "sb-book__row";
            li.appendChild(document.createElement("span")).textContent = l.title + " · " + (first ? strings.bookFirst : strings.bookMore);
            li.appendChild(document.createElement("span")).textContent = money(first ? l.rate[0] : l.rate[1]);
            ul.appendChild(li);
          }
        });
        box.appendChild(ul);
        bookList.appendChild(box);
      });
      return show;
    }

    // Each parcel's dates: made after its make days, at the door after the make days plus the days
    // on the way (the table's for that parcel and country, else Shopify's), in working days.
    function windows(list) {
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var dated = list.filter(function (p) {
        var t = p.transit || days;
        if (!t) return false;
        p.made = workingDaysFrom(today, p.print[0]);
        p.from = workingDaysFrom(today, p.print[0] + t[0]);
        p.to = workingDaysFrom(today, p.print[1] + t[1]);
        return true;
      });
      return dated.length && dated.length === list.length ? dated : null;
    }

    // What one parcel is doing on a day.
    function phaseOf(p, d) {
      if (d <= p.made) return "make";
      if (d < p.from) return "way";
      if (d <= p.to) return sameDay(d, p.from) ? "first" : sameDay(d, p.to) ? "last" : "arrive";
      return "done";
    }
    var TIP = { make: "tipMake", way: "tipWay", arrive: "tipArrive", first: "tipFirst", last: "tipLast", done: "tipDone" };

    // The day strip: today to the last arrival, every calendar day a cell, read as one path: made
    // (the fewest make days), on its way (to the earliest arrival), then the window it can arrive
    // in. Weekends are hatched: nothing moves on them. Every day carries a note of what happens, for
    // the pointer, a tap, and the arrow keys. The cells are built once and again only when the dates
    // change, and then without their entrance: a piece removed does not redraw the weeks.
    var tips = [];
    var active = 0;
    var drawnSig = "";
    var drawn = false;
    function drawDays(list) {
      if (!when || !daysEl) return;
      var lang = document.documentElement.lang || undefined;
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      var made = null, first = null, last = null;
      list.forEach(function (p) {
        if (!made || p.made < made) made = p.made;
        if (!first || p.from < first) first = p.from;
        if (!last || p.to > last) last = p.to;
      });
      // One note for the bag when every parcel keeps the same dates, else a line per parcel.
      var same = list.every(function (p) { return sameDay(p.made, list[0].made) && sameDay(p.from, list[0].from) && sameDay(p.to, list[0].to); });
      var sig = [made, first, last].map(function (x) { return x.getTime(); }).join(",");
      var cells = sig !== drawnSig;
      var html = "";
      tips = [];
      var d = new Date(today.getTime());
      var i = 0;
      while (d <= last && i < 120) {
        var phase = d <= made ? "make" : d < first ? "way" : "arrive";
        var rest = isRest(d);
        var mark = d.getDate() === 1 || i === 0;
        var lines = [];
        if (i === 0) lines.push(strings.tipToday);
        if (rest) lines.push(strings.tipRest);
        else if (i > 0) {
          if (same) lines.push(strings[TIP[phaseOf(list[0], d)]]);
          else list.forEach(function (p) { lines.push(p.name + ": " + strings[TIP[phaseOf(p, d)]]); });
        }
        tips.push({ date: dayLabel(d), lines: lines });
        if (cells) {
          html += '<li class="sb-day sb-day--' + phase + (rest ? " sb-day--rest" : "") + (i === 0 ? " sb-day--today" : "") + (mark ? " sb-day--mark" : "") +
            '" style="--i:' + Math.min(i, 40) + '" data-i="' + i + '">' +
            '<span class="sb-day__w">' + d.toLocaleDateString(lang, { weekday: "narrow" }) + "</span>" +
            '<span class="sb-day__n">' + d.getDate() + "</span>" +
            (mark ? '<span class="sb-day__m">' + d.toLocaleDateString(lang, { month: "short" }) + "</span>" : "") + "</li>";
        }
        d.setDate(d.getDate() + 1);
        i++;
      }
      if (cells) {
        hideTip();
        daysEl.classList.toggle("is-settled", drawn);
        daysEl.innerHTML = html;
        drawnSig = sig;
        if (active >= tips.length) active = 0;
      }
      drawn = true;
      daysEl.parentElement.setAttribute("aria-valuemax", String(tips.length - 1));
      valueOf(active);
      // A line per parcel under the strip: when each one arrives (the owner, 2026-10-16).
      if (listEl) {
        listEl.textContent = "";
        listEl.hidden = false;
        list.forEach(function (p) {
          var li = document.createElement("li");
          li.className = "sb-parcels__item";
          li.appendChild(document.createElement("strong")).textContent = p.name;
          var count = p.pieces === 1 ? strings.piecesOne || "1" : (strings.piecesMany || "{n}").replace("{n}", p.pieces);
          li.appendChild(document.createElement("span")).textContent = fill(strings.parcelLine, { count: count, from: dayLabel(p.from), to: dayLabel(p.to) });
          listEl.appendChild(li);
        });
      }
      when.hidden = false;
      console.log("📦 [State Update]: day strip", cells ? "drawn" : "kept", "·", i, "days,", list.length, "parcel(s)", same ? "on the same dates" : "on their own dates");
    }

    // The note over a day: placed above its cell (below it on the first row), kept inside the strip.
    function valueOf(i) {
      var frame = daysEl.parentElement;
      var t = tips[i];
      if (!t) return;
      frame.setAttribute("aria-valuenow", String(i));
      frame.setAttribute("aria-valuetext", t.date + ". " + t.lines.join(" "));
    }
    function showTip(i) {
      if (!tipEl || !tips[i]) return;
      var cell = daysEl.querySelector('[data-i="' + i + '"]');
      if (!cell) return;
      Array.prototype.forEach.call(daysEl.querySelectorAll(".is-pointed"), function (c) { c.classList.remove("is-pointed"); });
      cell.classList.add("is-pointed");
      tipEl.textContent = "";
      tipEl.appendChild(document.createElement("strong")).textContent = tips[i].date;
      tips[i].lines.forEach(function (line) { tipEl.appendChild(document.createElement("span")).textContent = line; });
      tipEl.hidden = false;
      var frame = daysEl.parentElement;
      var w = tipEl.offsetWidth;
      var h = tipEl.offsetHeight;
      var centre = cell.offsetLeft + cell.offsetWidth / 2;
      var left = Math.max(0, Math.min(frame.clientWidth - w, centre - w / 2));
      var above = cell.offsetTop - h - 10;
      var below = cell.getBoundingClientRect().top - h - 10 < 72;
      tipEl.style.left = left + "px";
      tipEl.style.top = (below ? cell.offsetTop + cell.offsetHeight + 10 : above) + "px";
      tipEl.style.setProperty("--x", centre - left + "px");
      tipEl.classList.toggle("is-below", below);
      valueOf(i);
    }
    function hideTip() {
      if (!tipEl || !daysEl) return;
      tipEl.hidden = true;
      Array.prototype.forEach.call(daysEl.querySelectorAll(".is-pointed"), function (c) { c.classList.remove("is-pointed"); });
    }
    // With a mouse the note follows the pointer and leaves with it, pressed or not (the owner,
    // 2026-10-16: a pressed day kept its note after the pointer had gone). A tap shows it until the
    // next tap elsewhere; the keys show it while the strip has the keyboard's focus.
    if (daysEl && tipEl) {
      var frame = daysEl.parentElement;
      var lastPointer = "mouse";
      daysEl.addEventListener("pointerdown", function (e) { lastPointer = e.pointerType; }, { passive: true });
      daysEl.addEventListener("pointerover", function (e) {
        if (e.pointerType !== "mouse") return;
        var cell = e.target.closest("[data-i]");
        if (cell) showTip(Number(cell.getAttribute("data-i")));
      });
      daysEl.addEventListener("pointerleave", function (e) { if (e.pointerType === "mouse") hideTip(); });
      daysEl.addEventListener("click", function (e) {
        var cell = e.target.closest("[data-i]");
        if (!cell) return;
        active = Number(cell.getAttribute("data-i"));
        console.log("👆 [Trigger]: day", active, "pressed by", lastPointer);
        if (lastPointer !== "mouse") showTip(active);
      });
      frame.addEventListener("focus", function () { if (frame.matches(":focus-visible")) showTip(active); });
      frame.addEventListener("blur", hideTip);
      frame.addEventListener("keydown", function (e) {
        var to = { ArrowRight: active + 1, ArrowLeft: active - 1, Home: 0, End: tips.length - 1 }[e.key];
        if (e.key === "Escape") return hideTip();
        if (to === undefined) return;
        e.preventDefault();
        active = Math.max(0, Math.min(tips.length - 1, to));
        showTip(active);
      });
      document.addEventListener("pointerdown", function (e) { if (!frame.contains(e.target)) hideTip(); }, { passive: true });
      window.addEventListener("resize", hideTip, { passive: true });
    }

    // The delivery and the total as the summary shows them; null: still being worked out.
    function showDelivery(cents) {
      var sub = Number(subtotalEl.getAttribute("data-sb-subtotal")) || 0;
      if (cents === null) {
        shipPrice.textContent = strings.shippingPending || "…";
        totalEl.textContent = money(sub);
        return;
      }
      shipPrice.textContent = cents === 0 ? strings.shippingFree || "Free" : money(cents);
      totalEl.textContent = money(sub + cents);
    }
    function showDates(list) {
      var dated = list.length ? windows(list) : null;
      if (!dated) {
        if (arrives) arrives.hidden = true;
        if (when) when.hidden = true;
        hideTip();
        return null;
      }
      var from = dated.reduce(function (a, p) { return !a || p.from < a ? p.from : a; }, null);
      var to = dated.reduce(function (a, p) { return !a || p.to > a ? p.to : a; }, null);
      if (arrives) {
        arrives.textContent = fill(strings.arrives, { from: dayLabel(from), to: dayLabel(to) });
        arrives.hidden = false;
      }
      drawDays(dated);
      return dated;
    }

    // Everything the lines decide, worked out again in the page at once: the subtotal, the parcels,
    // the count, each piece's delivery from the table and the dates. Shopify is asked afterwards.
    function refresh() {
      var t0 = performance.now();
      gen++;
      var count = 0;
      var sub = 0;
      root.querySelectorAll("[data-sb-line]").forEach(function (li) {
        count += qtyOf(li);
        sub += Number(li.getAttribute("data-line-price")) || 0;
      });
      var n = renumber();
      if (subtotalEl) {
        subtotalEl.setAttribute("data-sb-subtotal", String(sub));
        subtotalEl.textContent = money(sub);
      }
      if (parcelsEl) parcelsEl.textContent = n === 1 ? strings.parcelsOne : fill(strings.parcelsMany, { n: n });
      state.count = count;
      document.querySelectorAll("[data-sb-bag-count]").forEach(function (el) { el.textContent = count; });
      var empty = count === 0;
      if (emptyEl) emptyEl.hidden = !empty;
      if (summary) summary.hidden = empty;
      if (empty) {
        if (when) when.hidden = true;
        if (book) book.hidden = true;
        hideTip();
        console.log("📦 [State Update]: the bag is empty");
        return;
      }
      var list = parcels();
      if (none[country]) {
        shipPrice.textContent = strings.shippingNone || "—";
        totalEl.textContent = money(sub);
        paintSplit(list, false, 0);
        showDates([]);
      } else {
        var known = price(list);
        var ours = known && trusted[country] !== false ? list.reduce(function (s, p) { return s + p.price; }, 0) : null;
        showDelivery(ours);
        showDates(list);
        paintSplit(list, known, ours === null ? 0 : ours);
      }
      console.log("⏱ [Timing]: the bag worked out in place in", Math.round(performance.now() - t0), "ms:", count, "pieces,", n, "parcel(s),", money(sub));
      check();
    }

    // Shopify's rates for the bag as it now stands, after every change has reached it.
    function rates(where) {
      var q = "shipping_address%5Bcountry%5D=" + encodeURIComponent(where);
      var t0 = performance.now();
      console.log("🚀 [API Call]: shipping rates for", where);
      return json("/cart/prepare_shipping_rates.json?" + q, {}).catch(function () { return null; }).then(function () {
        return new Promise(function (resolve) {
          var tries = 0;
          (function poll() {
            tries++;
            json("/cart/async_shipping_rates.json?" + q).then(function (data) {
              if (data === null && tries < 16) return setTimeout(poll, 500);
              var all = (data && data.shipping_rates) || [];
              console.log("⏱ [Timing]: rates answered in", Math.round(performance.now() - t0), "ms after", tries, "polls");
              if (!all.length) return resolve({ none: true });
              var best = all.reduce(function (a, b) { return Number(a.price) <= Number(b.price) ? a : b; });
              resolve({ none: false, cents: Math.round(Number(best.price) * 100), days: best.delivery_days && best.delivery_days.length ? best.delivery_days.map(Number) : null });
            }).catch(function (err) {
              console.error("🛑 [bag-page]: rates failed", err);
              resolve(null);
            });
          })();
        });
      });
    }

    // Shopify checks the page's numbers a moment after the last change, and settles what differs.
    var checkTimer = null;
    function check() {
      clearTimeout(checkTimer);
      var mine = gen;
      var where = country;
      checkTimer = setTimeout(function () {
        queue.then(function () { return rates(where); }).then(function (answer) {
          if (!answer || mine !== gen || where !== country) return;
          var list = parcels();
          none[where] = answer.none;
          if (answer.none) {
            console.log("🛑 [Delivery]: Shopify delivers nowhere in", where);
            shipPrice.textContent = strings.shippingNone || "—";
            totalEl.textContent = money(Number(subtotalEl.getAttribute("data-sb-subtotal")) || 0);
            paintSplit(list, false, 0);
            showDates([]);
            return;
          }
          days = answer.days || setDays || days;
          var known = price(list);
          var ours = known ? list.reduce(function (s, p) { return s + p.price; }, 0) : null;
          trusted[where] = ours === answer.cents;
          console.log(trusted[where] ? "✅ [Delivery]: Shopify agrees," : "🛑 [Delivery]: Shopify says otherwise,", money(answer.cents), "against the table's", ours === null ? "nothing" : money(ours));
          showDelivery(answer.cents);
          showDates(list);
          paintSplit(list, known, answer.cents);
        });
      }, 350);
    }

    // Shopify's cart after a change: the exact line prices for the lines it agrees on, and the
    // corner and header counts once nothing else is on its way.
    function reconcile(cart) {
      if (!cart || !cart.items) return;
      var changed = false;
      cart.items.forEach(function (item) {
        var li = Array.prototype.find.call(root.querySelectorAll("[data-sb-line]"), function (el) { return el.getAttribute("data-sb-line") === item.key; });
        if (!li || li.__t || qtyOf(li) !== item.quantity) return;
        if (Number(li.getAttribute("data-line-price")) !== item.final_line_price) changed = true;
        li.setAttribute("data-unit", String(item.final_price));
        li.setAttribute("data-line-price", String(item.final_line_price));
        var total = li.querySelector("[data-sb-line-total]");
        if (total) total.textContent = money(item.final_line_price);
      });
      if (busy === 0) paint(cart);
      if (changed) {
        console.log("📦 [State Update]: Shopify's line prices differ, the bag is worked out again");
        refresh();
      }
    }

    function setQty(li, q) {
      li.setAttribute("data-qty", String(q));
      var unit = Number(li.getAttribute("data-unit")) || 0;
      li.setAttribute("data-line-price", String(unit * q));
      var count = li.querySelector(".sb-line__count");
      if (count) count.textContent = q;
      var total = li.querySelector("[data-sb-line-total]");
      if (total) total.textContent = money(unit * q);
      var minus = li.querySelector('[data-sb-step="-1"]');
      if (minus) minus.disabled = q <= 1;
    }

    // Sends a line's quantity once the presses stop (a burst of + is one request).
    function commit(li) {
      clearTimeout(li.__t);
      li.__t = null;
      var key = li.getAttribute("data-sb-line");
      var q = qtyOf(li);
      if (q === li.__ok) return;
      var t0 = performance.now();
      send(function () {
        console.log("🚀 [API Call]: change", key, "→", q);
        return json(routes.cartChange, { id: key, quantity: q });
      }).then(function (cart) {
        li.__ok = q;
        console.log("⏱ [Timing]: Shopify holds", q, "of", li.getAttribute("data-title"), Math.round(performance.now() - t0), "ms after it was sent");
        reconcile(cart);
      }).catch(function (err) {
        console.error("🛑 [bag-page]: change failed", err);
        if (li.isConnected && !li.__t) {
          setQty(li, li.__ok);
          refresh();
        }
        failed();
      });
    }

    function step(li, d, btn) {
      var had = qtyOf(li);
      var want = had + d;
      if (want < 1) return;
      // One more piece than its parcel holds is refused here, before any request.
      var max = Number(bagCfg.maxPerParcel) || 0;
      if (d > 0 && max) {
        var box = li.closest("[data-sb-parcel]");
        var pieces = box ? Array.prototype.reduce.call(box.querySelectorAll("[data-sb-line]"), function (s, el) { return s + qtyOf(el); }, 0) : 0;
        if (pieces + d > max) {
          console.log("🛑 [Blocked]: parcel", box && box.getAttribute("data-sb-parcel"), "holds", pieces, "of", max);
          if (fullEl) {
            clearTimeout(fullEl.__t);
            fullEl.textContent = strings.parcelFull || "";
            fullEl.hidden = false;
          }
          return;
        }
      }
      if (d < 0 && fullEl) fullEl.hidden = true;
      console.log("👆 [Trigger]: quantity", had, "→", want, "for", li.getAttribute("data-title"));
      setQty(li, want);
      // A − that disables under the finger hands the focus to its + (VANTA).
      if (want <= 1 && btn && btn.disabled) {
        var plus = li.querySelector('[data-sb-step="1"]');
        if (plus) plus.focus({ preventScroll: true });
      }
      refresh();
      clearTimeout(li.__t);
      li.__t = setTimeout(function () { commit(li); }, 280);
    }

    // Remove: the line gives way to "Put it back" in its own place, at once (VANTA's bag).
    function remove(li) {
      var t0 = performance.now();
      clearTimeout(li.__t);
      li.__t = null;
      var key = li.getAttribute("data-sb-line");
      var q = qtyOf(li);
      var title = li.getAttribute("data-title") || "";
      console.log("👆 [Trigger]: removed", title, "×", q);
      var row = document.createElement("li");
      row.className = "sb-gone";
      row.setAttribute("data-sb-undo", "");
      var p = row.appendChild(document.createElement("p"));
      p.className = "sb-gone__text";
      p.appendChild(document.createElement("span")).textContent = fill(strings.removed, { title: q > 1 ? title + " × " + q : title });
      var back = p.appendChild(document.createElement("button"));
      back.type = "button";
      back.className = "sb-gone__back";
      back.setAttribute("data-sb-put-back", "");
      back.textContent = strings.putBack || "Put it back";
      row.__line = li;
      row.__qty = q;
      li.replaceWith(row);
      back.focus({ preventScroll: true });
      refresh();
      console.log("⏱ [Timing]: remove shown in", Math.round(performance.now() - t0), "ms");
      send(function () {
        console.log("🚀 [API Call]: change", key, "→ 0");
        return json(routes.cartChange, { id: key, quantity: 0 });
      }).then(function (cart) {
        li.__ok = 0;
        console.log("⏱ [Timing]: Shopify took it out", Math.round(performance.now() - t0), "ms after the press");
        reconcile(cart);
      }).catch(function (err) {
        console.error("🛑 [bag-page]: remove failed", err);
        if (row.isConnected) {
          row.replaceWith(li);
          refresh();
        }
        failed();
      });
    }

    function putBack(row) {
      var t0 = performance.now();
      var li = row.__line;
      var q = row.__qty;
      if (!li) return;
      console.log("👆 [Trigger]: put back", li.getAttribute("data-title"), "×", q);
      setQty(li, q);
      row.replaceWith(li);
      var rm = li.querySelector("[data-sb-remove]");
      if (rm) rm.focus({ preventScroll: true });
      refresh();
      send(function () {
        console.log("🚀 [API Call]: add", li.getAttribute("data-variant"), "×", q);
        return json(routes.cartAdd, { items: [{ id: Number(li.getAttribute("data-variant")), quantity: q }] });
      }).then(function (added) {
        var items = Array.isArray(added) ? added : added && added.items;
        if (items && items[0] && items[0].key) li.setAttribute("data-sb-line", items[0].key);
        li.__ok = q;
        console.log("⏱ [Timing]: Shopify has it back", Math.round(performance.now() - t0), "ms after the press");
        return json(routes.cartJs).then(reconcile);
      }).catch(function (err) {
        console.error("🛑 [bag-page]: put back failed", err);
        if (li.isConnected) {
          li.replaceWith(row);
          refresh();
        }
        failed();
      });
    }

    root.addEventListener("click", function (e) {
      var b = e.target.closest("button");
      if (!b || !root.contains(b)) return;
      if (b.hasAttribute("data-sb-put-back")) {
        var row = b.closest("[data-sb-undo]");
        if (row) putBack(row);
        return;
      }
      var li = b.closest("[data-sb-line]");
      if (!li) return;
      if (b.hasAttribute("data-sb-remove")) return remove(li);
      if (b.hasAttribute("data-sb-step")) return step(li, Number(b.getAttribute("data-sb-step")), b);
    });

    // Checkout waits for every change to reach Shopify, and rings the till: the sound starts on the
    // press and the checkout opens 650ms later, after the "ka" and the bell's strike, so the ring is
    // heard while the page leaves (the owner, 2026-10-16: the chime when a piece goes in, ka-ching
    // when paying). Shopify's own checkout pages cannot play anything. A sound not ready within
    // 300ms is skipped: checkout is never held up by it.
    function till() {
      if (!bagCfg.checkoutSoundUrl || SB.reduce) return Promise.resolve(0);
      ensureAudio();
      return new Promise(function (resolve) {
        var settled = false;
        var giveUp = setTimeout(function () {
          if (settled) return;
          settled = true;
          console.log("🛑 [Sound]: not ready in time, checkout opens without it");
          resolve(0);
        }, 300);
        soundBuffer(bagCfg.checkoutSound, bagCfg.checkoutSoundUrl).then(function (buffer) {
          if (settled) return;
          settled = true;
          clearTimeout(giveUp);
          if (!buffer) return resolve(0);
          if (audio.state === "suspended") audio.resume();
          console.log("🔔 [Sound]:", bagCfg.checkoutSound, "at checkout");
          play(buffer);
          resolve(650);
        });
      });
    }
    if (form) {
      if (bagCfg.checkoutSoundUrl) soundData(bagCfg.checkoutSound, bagCfg.checkoutSoundUrl);
      form.addEventListener("submit", function (e) {
        var by = e.submitter;
        if (!by || !by.hasAttribute("data-sb-checkout") || form.__ringing === "done") return;
        e.preventDefault();
        if (form.__ringing) return;
        form.__ringing = "on";
        by.setAttribute("aria-busy", "true");
        var t0 = performance.now();
        console.log("👆 [Trigger]: Checkout pressed");
        root.querySelectorAll("[data-sb-line]").forEach(function (li) { if (li.__t) commit(li); });
        var ring = till().then(function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); });
        Promise.all([ring, queue]).then(function () {
          form.__ringing = "done";
          console.log("🚀 [Checkout]: leaving for checkout", Math.round(performance.now() - t0), "ms after the press");
          if (form.requestSubmit) return form.requestSubmit(by);
          var field = document.createElement("input");
          field.type = "hidden";
          field.name = by.name;
          field.value = by.value || "";
          form.appendChild(field);
          form.submit();
        });
      });
    }

    if (shipTo) {
      shipTo.addEventListener("change", function () {
        console.log("👆 [Trigger]: deliver to", shipTo.value);
        country = shipTo.value;
        days = setDays;
        refresh();
      });
    }
    root.querySelectorAll("[data-sb-line]").forEach(function (li) { li.__ok = qtyOf(li); });
    if (root.querySelector("[data-sb-line]")) refresh();
  });

  // sb-motion.js may already have armed the page before this file ran.
  SB.arm(document);
  console.log("✅ [Boot]: sb-bag ready, bag holds", state.count);
})();
